import { Button, LoadingSpinner, SegmentedControl, Tooltip } from '@neo4j-ndl/react';
import { Squares2X2IconOutline, TableCellsIconOutline } from '@neo4j-ndl/react/icons';
import { type FleetManagementTypes, useActiveProject, useSetting } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { SearchField } from '@nx/ui';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { ExploreIconOutline } from '../../../upx/src/components/icons';
import { ApiErrorBanner } from '../shared/components';
import { DeploymentInspectDrawer } from './deployments/deployment-inspect-drawer';
import { DbmsList } from './deployments/deployments-list';
import { DbmsTable } from './deployments/deployments-table';
import { DbmsGraph } from './graph/deployments-graph';
import { useDbmss } from './hooks/use-dbmss';
import { usePermissions } from './hooks/use-permissions';
import { ConnectDeploymentWizardModal } from './modals/connect-deployment-wizard-modal';

const DeploymentsView = () => {
  const activeProject = useActiveProject();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [deploymentDisplayType, setDeploymentDisplayType] = useSetting('ops', 'deploymentDisplay');
  const [isConnectDeploymentModalOpen, setConnectDeploymentModalOpen] = useState<boolean>(false);

  const { dbmss, isDbmssLoading, error } = useDbmss({ shouldPoll: true });

  const [dbmsDetails, setDbmsDetails] = useState<FleetManagementTypes.DbmsDetails | null>(null);

  const handleDisplayDbmsDetails = useCallback((dbms: FleetManagementTypes.DbmsDetails) => {
    setDbmsDetails(dbms);
  }, []);

  const handleCloseDbmsDetails = useCallback(() => {
    setDbmsDetails(null);
  }, []);

  const isTokenCreationAllowed = usePermissions().isAllowed('tokens', 'write');

  if (error) {
    return <ApiErrorBanner className="m-4" error={error} />;
  }

  if (isDbmssLoading) {
    return <LoadingSpinner size="large" className="absolute left-1/2 top-1/2" />;
  }

  return (
    <>
      {isNotNullish(dbmsDetails) && <DeploymentInspectDrawer dbms={dbmsDetails} onClose={handleCloseDbmsDetails} />}
      <div className="relative mt-2 flex h-full flex-col">
        <div className="mb-2 flex h-14 w-full items-center justify-between gap-4 py-[10px]">
          <div className="flex basis-[400px] gap-2">
            <SearchField
              className="min-w-36 grow"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              htmlAttributes={{ 'aria-label': 'Search for DBMSs' }}
            />
          </div>
          <div className="flex shrink-0 gap-4">
            <SegmentedControl
              onChange={(changedId) => setDeploymentDisplayType(changedId)}
              hasOnlyIcons
              size="small"
              selected={deploymentDisplayType}
            >
              <SegmentedControl.Item
                value="table"
                htmlAttributes={{
                  'aria-label': 'View instances in Table view',
                }}
              >
                <TableCellsIconOutline className="size-4" />
              </SegmentedControl.Item>
              <SegmentedControl.Item
                value="graph"
                htmlAttributes={{
                  'aria-label': 'View instances in Hierarchy view',
                }}
              >
                <ExploreIconOutline className="size-4" />
              </SegmentedControl.Item>
              <SegmentedControl.Item
                value="list"
                htmlAttributes={{
                  'aria-label': 'View instances in List view',
                }}
              >
                <Squares2X2IconOutline className="size-4" />
              </SegmentedControl.Item>
            </SegmentedControl>
            <Tooltip type={'simple'} isDisabled={isTokenCreationAllowed}>
              <Tooltip.Trigger>
                <Button
                  fill="outlined"
                  color="neutral"
                  onClick={() => {
                    void navigate(`/projects/${activeProject.id}/instances/token-management`);
                  }}
                  isDisabled={!isTokenCreationAllowed}
                >
                  Token management
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>Project admin role is required to manage tokens</Tooltip.Content>
            </Tooltip>
            <Tooltip type={'simple'} isDisabled={isTokenCreationAllowed}>
              <Tooltip.Trigger>
                <Button
                  fill="filled"
                  color="primary"
                  isDisabled={!isTokenCreationAllowed}
                  onClick={() => setConnectDeploymentModalOpen(true)}
                >
                  Register self-managed deployment
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>Project admin role is required to manage deployments</Tooltip.Content>
            </Tooltip>
          </div>
          <ConnectDeploymentWizardModal
            isOpen={isConnectDeploymentModalOpen}
            closeModal={() => setConnectDeploymentModalOpen(false)}
          />
        </div>
        {deploymentDisplayType === 'table' && (
          <DbmsTable
            dbmss={dbmss}
            filter={filter}
            setFilter={setFilter}
            handleDisplayDbmsDetails={handleDisplayDbmsDetails}
          />
        )}
        {deploymentDisplayType === 'graph' && (
          <DbmsGraph
            dbmss={dbmss}
            filter={filter}
            containerClassName={'h-[calc(100vh-300px)]'}
            isInDetailsView={false}
          />
        )}
        {deploymentDisplayType === 'list' && (
          <DbmsList dbmss={dbmss} filter={filter} handleDisplayDbmsDetails={handleDisplayDbmsDetails} />
        )}
      </div>
    </>
  );
};

export default DeploymentsView;
